import { Box, IconButton, Popover } from '@mui/material';
import type { PopoverActions } from '@mui/material';
import type { PlanNode } from '@timelog/pm-shared';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { AllocationEditor } from './AllocationEditor';
import { usePlanEditor } from './editor';

import { MaterialSymbol } from '@/components/MaterialSymbol';

export type ResourcesPopoverProps = {
  node: PlanNode;
  /** Where to anchor; the popover flips/clamps itself against the viewport. */
  anchorEl: HTMLElement | null;
  /**
   * Which edge of the anchor the popover hangs from. 'right' suits a control
   * at the row's right end (the kebab); 'left' opens rightwards from one at
   * the row's left (the resources cell), which would otherwise run off the
   * side and be clamped back.
   */
  align?: 'left' | 'right';
};

/**
 * The single resource editor (v5, Area 2 / decision 7): a popover wrapping the
 * allocation table, anchored to whatever opened it. Opened from the row kebab
 * or the resources cell; closed by its button, click-away or Escape.
 *
 * It used to anchor to the ROW, which meant it always appeared at the row's
 * bottom-right — beside the kebab, however you had opened it. Opening it from
 * the resources cell at the other end of the row threw the eye across the
 * whole row to find it.
 */
export const ResourcesPopover = ({ node, anchorEl, align = 'right' }: ResourcesPopoverProps) => {
  const { state, dispatch } = usePlanEditor();
  const { t } = useTranslation('pmPlanningPanel');
  const open = state.resourcesNodeId === node.id && anchorEl !== null;
  const close = () => dispatch({ type: 'CLOSE_RESOURCES' });

  /*
    MUI places a popover once, on open, from the paper's height at that
    moment. The editor grows after that — a line added, the over-budget
    warning appearing as hours are typed — and a popover opened low on the
    page then ran off the bottom with Save out of reach (2026-10-20). Every
    change in the content's height asks MUI to place it again, which clamps it
    back inside the viewport (and the paper scrolls if it is taller still).
  */
  const actionRef = useRef<PopoverActions>(null);
  const [content, setContent] = useState<HTMLElement | null>(null);
  const contentRef = useCallback((element: HTMLElement | null) => setContent(element), []);
  useEffect(() => {
    if (!content) return undefined;
    const observer = new ResizeObserver(() => actionRef.current?.updatePosition());
    observer.observe(content);
    return () => observer.disconnect();
  }, [content]);

  return (
    <Popover
      open={open}
      action={actionRef}
      anchorEl={anchorEl}
      onClose={close}
      anchorOrigin={{ vertical: 'bottom', horizontal: align }}
      transformOrigin={{ vertical: 'top', horizontal: align }}
      data-automation-id="resources-popover"
      slotProps={{
        paper: {
          // Escape must close the popover even while one of its raw inputs has
          // focus — useKeyboardNav deliberately ignores INPUT targets (tree
          // navigation), so the popover owns its own Escape. stopPropagation
          // keeps the plan editor's global Escape (clear selection) out of it.
          onKeyDown: (event) => {
            if (event.key === 'Escape') {
              event.stopPropagation();
              close();
            }
          },
          sx: {
            width: 470,
            maxWidth: 'calc(100vw - 32px)',
            p: '12px 14px 13px',
            border: 'var(--border-width-thin) solid var(--border-subtle)',
            borderRadius: 'var(--border-radius-medium)',
            boxShadow: 'var(--shadow-small)',
          },
        },
      }}
    >
      {/*
        The id on the Popover itself lands on MUI's Modal root, which spans the
        whole viewport — so it answers "is it open?" but never "where is it?".
        This row is inside the paper and spans its content width, so it is what
        a positioning assertion can measure.
      */}
      <Box
        data-automation-id="resources-popover-header"
        sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}
      >
        <MaterialSymbol name="group_add" size={17} color="var(--color-purple-80)" />
        <Box
          component="span"
          sx={{
            flex: 1,
            minWidth: 0,
            fontSize: 14,
            fontWeight: 500,
            color: 'var(--fg-1)',
            whiteSpace: 'nowrap',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
          }}
        >
          {t('resourcesPopover.Title', { name: node.name })}
        </Box>
        <IconButton
          size="tiny"
          title={t('shared.Close')}
          aria-label={t('shared.Close')}
          data-automation-id="resources-popover-close"
          onClick={close}
        >
          <MaterialSymbol name="close" size={16} />
        </IconButton>
      </Box>
      {node.allocations.length === 0 ? (
        <Box sx={{ fontSize: 11, fontStyle: 'italic', color: 'var(--fg-3)', mb: 1 }}>
          {t('resourcesPopover.NoResourcesYet')}
        </Box>
      ) : null}
      {/*
        Rendered only while open, so the editor mounts fresh on each open and
        can capture the task's budget at mount (see AllocationEditor).
      */}
      {open ? (
        <Box ref={contentRef}>
          <AllocationEditor node={node} onRequestClose={close} />
        </Box>
      ) : null}
    </Popover>
  );
};
